// Copyright 2016 Sorama B.V.

import { DOMExtensions } from "extensions/DOMExtensions";
import { IIndexableObject } from "libraries/IndexedDb/IIndexableObject";

class DbPromise {
    static objectStore(
        database: IDBDatabase,
        objectStoreName: string,
        accessMode: IDBTransactionMode,
        action: (objectStore: IDBObjectStore) => void,
        doneAction: () => void = null,
        rejectAction: (e: unknown) => void = null
    ): void {
        // Start Transaction
        const transaction = database.transaction(objectStoreName, accessMode);
        const objectStore = transaction.objectStore(objectStoreName);
        action(objectStore);

        transaction.oncomplete = (e) => {
            if (doneAction) {
                try {
                    doneAction();
                } catch (e) {
                    rejectAction(e);
                }
            }
        };
        transaction.onerror = (e) => {
            rejectAction(e);
        };
        transaction.onabort = (e) => {
            rejectAction(e);
        };
    }

    static request<T>(request: IDBRequest, action: (obj: T) => void, rejectAction: (e: unknown) => void = null): void {
        request.onsuccess = (e) => {
            const obj = request.result as T;
            try {
                action(obj);
            } catch (e) {
                rejectAction(e);
            }
        };

        request.onerror = (e) => {
            rejectAction(e);
        };
    }

    static cursor(
        request: IDBRequest,
        oncursor: (cursor: IDBCursor) => void,
        doneAction: () => void,
        rejectAction: (e: unknown) => void = null
    ): void {
        // onsuccess can be called multiple times
        request.onsuccess = (e) => {
            const cursor: IDBCursor = (e.target as IDBRequest).result;

            if (cursor) {
                try {
                    oncursor(cursor);
                } catch (e) {
                    rejectAction(e);
                }

                cursor.continue();
            } else {
                doneAction();
            }
        };

        request.onerror = (e) => {
            rejectAction(request.error);
        };
    }
}

export interface ObjectStoreDefinition {
    name: string;
    generateKey: boolean;
}

export abstract class IndexedDb {
    //#region Injected Services
    private readonly databaseName: string;
    private readonly databaseVersion: number;
    private readonly storableObjectNames: ObjectStoreDefinition[];
    //#endregion

    //#region Private Fields
    private databasePromise: Promise<IDBDatabase>;
    //#endregion

    //#region Constructor and Initialisation
    constructor(databaseName: string, databaseVersion: number, storableObjectNames: ObjectStoreDefinition[]) {
        this.databaseName = databaseName;
        this.databaseVersion = databaseVersion;
        this.storableObjectNames = storableObjectNames;

        this.openDatabase();
    }

    private openDatabase(): void {
        this.databasePromise = new Promise<IDBDatabase>((resolveOpen, rejectOpen) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const indexedDB: IDBFactory =
                window.indexedDB ||
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).mozIndexedDB ||
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).webkitIndexedDB ||
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).msIndexedDB;

            if (!indexedDB) {
                throw new Error(
                    "IndexedDb is not available or blocked by the browser. Some features may not work as expected."
                );
            }

            const request = indexedDB.open(this.databaseName, this.databaseVersion);

            // Called when succesfully openened db
            request.onsuccess = (event) => {
                const db = request.result as IDBDatabase;

                db.onerror = (e) => rejectOpen(e);
                db.onabort = (e) => rejectOpen(e);

                // check if all types are in database
                for (const storableObjectName of this.storableObjectNames) {
                    if (!db.objectStoreNames.contains(storableObjectName.name)) {
                        db.close();
                        rejectOpen(new Error("Requested ObjectStores are not in the database. Is version increased?"));
                        return;
                    }
                }

                resolveOpen(db);
            };

            // Called first run or if dB is outdated
            request.onupgradeneeded = (event) => {
                // Add all requests to the transaction. The request are processed once this scope is done, so we can just add all.
                const db = request.result as IDBDatabase;

                db.onerror = (e) => rejectOpen(e);
                db.onabort = (e) => rejectOpen(e);

                // Optional stuff before upgrade
                this.beforeUpgrade(event, db);

                // Delete all removed objectStores
                const allObjectStoreNames: string[] = [];
                for (let i = 0; i < db.objectStoreNames.length; i++) {
                    // Store all names first since deleting an object store will also remove it from db.objectStoreNames which messes with itteration
                    allObjectStoreNames.push(db.objectStoreNames.item(i));
                }
                for (let i = 0; i < allObjectStoreNames.length; i++) {
                    if (this.storableObjectNames.findIndex((x) => x.name === allObjectStoreNames[i]) === -1)
                        db.deleteObjectStore(allObjectStoreNames[i]);
                }

                // Create stores for all new objectStores
                this.storableObjectNames.map((objectStoreDefinition) => {
                    // createObjectStore can only be called in the onupgradeneeded!
                    if (!db.objectStoreNames.contains(objectStoreDefinition.name)) {
                        db.createObjectStore(objectStoreDefinition.name, {
                            autoIncrement: objectStoreDefinition.generateKey,
                        });
                    }
                });

                this.afterUpgrade(event, db);
            };

            request.onerror = (e) => {
                rejectOpen(request.error);
            };
        }).catch((e) => {
            throw DOMExtensions.errorFromDOMException(e);
        });
    }
    //#endregion

    /**
     * Gets all objects from an objectStore.
     * @param objectStoreName The name of the objectStore to get all objects from.
     * @param userId The userId to get all objects from. If not specified, defaults to the userId of the currently logged in user.
     * @returns The requested objects.
     */
    public getAll<TObject extends IIndexableObject>(
        objectStoreName: string,
        userId: string = null
    ): Promise<TObject[]> {
        return this.getObjectStoreDefinition(objectStoreName).then((storedefinition) => {
            return this.useDatabase<TObject[]>(storedefinition.name, (database, resolve, reject) => {
                const results: TObject[] = [];

                DbPromise.objectStore(
                    database,
                    storedefinition.name,
                    "readonly",
                    (objectStore) => {
                        const cursorRequest = objectStore.openCursor();

                        DbPromise.cursor(
                            cursorRequest,
                            (cursor) => {
                                DbPromise.request<TObject>(
                                    objectStore.get(cursor.key),
                                    (obj) => {
                                        if (!obj) {
                                            // Just return nothing, were not intereseted in a reject (will break chain)
                                            return;
                                        }

                                        // Set id to key value
                                        if (storedefinition.generateKey) {
                                            obj.id = cursor.key.toString();
                                        }

                                        results.push(obj);
                                    },
                                    reject
                                );
                            },
                            () => resolve(results),
                            reject
                        );
                    },
                    null,
                    reject
                );
            });
        });
    }

    /**
     * Gets and object from an objectStore.
     * @param key The key of the object to get
     * @param objectStoreName The name of the objectStore to get the object from.
     * @param userId The userId to get the object from. If not specified, defaults to the userId of the currently logged in user.
     * @returns The requested object.
     */
    public getObject<TObject extends IIndexableObject>(
        key: string,
        objectStoreName: string,
        userId: string = null
    ): Promise<TObject> {
        return this.getObjectStoreDefinition(objectStoreName).then((storedefinition) => {
            const typedKey = storedefinition.generateKey ? Number.parseInt(key) : key;
            return this.useDatabase(storedefinition.name, (database, resolve, reject) => {
                DbPromise.objectStore(
                    database,
                    storedefinition.name,
                    "readonly",
                    (objectStore) => {
                        const request = objectStore.get(typedKey);

                        DbPromise.request<TObject>(
                            request,
                            (obj) => {
                                if (!obj) {
                                    throw new Error("Object not found");
                                }

                                // Set id to key value
                                if (storedefinition.generateKey) {
                                    obj.id = typedKey.toString();
                                }

                                resolve(obj);
                            },
                            reject
                        );
                    },
                    null,
                    reject
                );
            });
        });
    }

    /**
     * Adds an object to an objectStore.
     * @param obj The object to add.
     * @param objectStoreName The name of the objectStore to store the object in.
     * @returns The id of the added object.
     */
    public addObject<TObject extends IIndexableObject>(obj: TObject, objectStoreName: string): Promise<string> {
        return this.getObjectStoreDefinition(objectStoreName).then((storedefinition) => {
            const typedKey = obj.id ? (storedefinition.generateKey ? Number.parseInt(obj.id) : obj.id) : undefined;
            return this.useDatabase(storedefinition.name, (database, resolve, reject) => {
                DbPromise.objectStore(
                    database,
                    storedefinition.name,
                    "readwrite",
                    (objectStore) => {
                        const req = typedKey ? objectStore.count(typedKey) : objectStore.count();

                        DbPromise.request<number>(
                            req,
                            (count) => {
                                if (typedKey && count) {
                                    throw new Error("Object with key " + typedKey + " already exists.");
                                } else {
                                    const request = storedefinition.generateKey
                                        ? objectStore.add(obj)
                                        : objectStore.add(obj, obj.id);
                                    DbPromise.request<string>(
                                        request,
                                        (key) => {
                                            resolve(storedefinition.generateKey ? key.toString() : key);
                                        },
                                        reject
                                    );
                                }
                            },
                            reject
                        );
                    },
                    null,
                    reject
                );
            });
        });
    }

    /**
     * Updates (or adds, if it does not exsit yet) an object in an objectStore.
     * @param obj The object to update.
     * @param objectStoreName The name of the objectStore to update the object in.
     * @returns The id of the updated (or added) object.
     */
    public putObject<TObject extends IIndexableObject>(obj: TObject, objectStoreName: string): Promise<string> {
        return this.getObjectStoreDefinition(objectStoreName).then((storedefinition) => {
            const typedKey = obj.id ? (storedefinition.generateKey ? Number.parseInt(obj.id) : obj.id) : undefined;

            if (!typedKey) {
                return Promise.reject("The id of the object to put is not specified.");
            }

            return this.useDatabase(storedefinition.name, (database, resolve, reject) => {
                DbPromise.objectStore(
                    database,
                    storedefinition.name,
                    "readwrite",
                    (objectStore) => {
                        const request = objectStore.put(obj, typedKey);

                        DbPromise.request<string>(
                            request,
                            (key) => {
                                resolve(storedefinition.generateKey ? key.toString() : key);
                            },
                            reject
                        );
                    },
                    null,
                    reject
                );
            });
        });
    }

    /**
     * Removes an object from an objectStore.
     * @param key The key of the object to remove.
     * @param objectStoreName The name of the objectStore the object is located in.
     * @param userId The userId of the user owning the object. If not specified, defaults to the userId of the currently logged in user.
     */
    public removeObject<TObject extends IIndexableObject>(
        key: string,
        objectStoreName: string,
        userId: string = null
    ): Promise<void> {
        return this.getObjectStoreDefinition(objectStoreName).then((storedefinition) => {
            const typedKey = storedefinition.generateKey ? Number.parseInt(key) : key;
            return this.useDatabase(storedefinition.name, (database, resolve, reject) => {
                DbPromise.objectStore(
                    database,
                    storedefinition.name,
                    "readwrite",
                    (objectStore) => {
                        const request = objectStore.get(typedKey);

                        DbPromise.request<TObject>(
                            request,
                            (obj: TObject) => {
                                if (!obj) {
                                    throw new Error("Object not found");
                                }

                                DbPromise.request<void>(
                                    objectStore.delete(typedKey),
                                    () => {
                                        resolve(undefined);
                                    },
                                    reject
                                );
                            },
                            reject
                        );
                    },
                    null,
                    reject
                );
            });
        });
    }

    /**
     * Removes all objects from an objectStore.
     * @param objectStoreName The name of the objectStore to remove all objects from.
     * @param userId The userId of the user owning the objects. If not specified, defaults to the userId of the currently logged in user.
     */
    public clearObjectStore<TObject extends IIndexableObject>(
        objectStoreName: string,
        userId: string = null
    ): Promise<void> {
        return this.getAll<TObject>(objectStoreName, userId)
            .then((objects) => {
                const promises = objects.map((obj) => {
                    return this.removeObject(obj.id, objectStoreName, userId).catch(); // Ignore failures
                });

                return Promise.all(promises).then(() => {});
            })
            .catch((e) => {
                throw DOMExtensions.errorFromDOMException(e);
            });
    }

    //#region Helper Methods
    private useDatabase<T>(
        objectStoreName: string,
        usage: (database: IDBDatabase, resolve: (value: T) => void, reject: (value: unknown) => void) => void
    ): Promise<T> {
        // Check if objectStoreName is in database
        if (!this.storableObjectNames.some((x) => x.name == objectStoreName)) {
            return Promise.reject(new Error("ObjectStoreName type is not in database"));
        }

        return this.databasePromise.then((database) => {
            return new Promise<T>((resolve, reject) => {
                try {
                    usage(database, resolve, reject);
                } catch (e) {
                    reject(e);
                }
            });
        });
    }

    //#endregion

    //#region Abstract Methods
    protected abstract beforeUpgrade(e: IDBVersionChangeEvent, db: IDBDatabase): void;
    protected abstract afterUpgrade(e: IDBVersionChangeEvent, db: IDBDatabase): void;
    //#endregion

    private getObjectStoreDefinition(name: string): Promise<ObjectStoreDefinition> {
        const objectStoreDefinition = this.storableObjectNames.find((x) => x.name === name);
        if (!objectStoreDefinition) {
            return Promise.reject("Invalid object store name.");
        }

        return Promise.resolve(objectStoreDefinition);
    }
}
